import { useField, useFormikContext } from "formik";
import { PhoneInput, PhoneInputRefType, CountryIso2 } from "react-international-phone";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface PhoneInputProps {
    name: string;
    placeholder?: string;
    value?: string;
    key?: string;
    disabled?: boolean;
    phoneRef?: React.RefObject<PhoneInputRefType>;
    id?: string;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (phone: string) => void;
    defaultCountry?: CountryIso2;
}

function getValueByPath(obj: Record<string, any>, path: string) {
    const keys = path.split(".");
    return keys.reduce((acc, key) => acc && acc[key], obj);
}

const PhonePicker: React.FC<PhoneInputProps> = ({
    name,
    placeholder,
    value,
    key,
    disabled,
    phoneRef,
    id,
    onBlur,
    onChange,
    defaultCountry,
}) => {
    const { t } = useTranslation();
    const phoneInputRef = useRef<PhoneInputRefType>(null);
    const [field, meta] = useField(name);
    const { setFieldValue, errors, touched, setFieldTouched } = useFormikContext<any>();
    const [phone, setPhone] = useState(value || "");
    const ref = phoneRef || phoneInputRef;

    const isInvalid =
        (errors[name] && touched[name]) || (getValueByPath(errors, name) && getValueByPath(touched, name));

    const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
        await setFieldTouched(name, true);
        onBlur?.(e);
    };

    useEffect(() => {
        const safeValuePicker = value || "";
        const safeValueFormic = value || null;

        setPhone(safeValuePicker);
        setFieldValue(name, safeValueFormic);
    }, [value, name, setFieldValue]);

    return (
        <div className="position-relative" data-error-field={name} data-phone-input="true">
            <PhoneInput
                ref={ref}
                key={key}
                name={name}
                className="fs-7 height-100 w-100"
                placeholder={t("Enter phone number")}
                inputClassName={`form-control fs-7 rounded-1 text-truncate ${isInvalid ? "border-danger" : "border-secondary"}`}
                inputStyle={{ height: "49px", paddingLeft: "65px", fontSize: "16px" }}
                countrySelectorStyleProps={{
                    className: "position-absolute",
                    style: { height: "49px" },
                    buttonClassName: `h-100 rounded-start-1 ps-2 pe-1 ${isInvalid ? "border-danger" : "border-secondary"}`,
                }}
                defaultCountry={defaultCountry}
                value={phone}
                onChange={(phone, meta) => {
                    setPhone(phone || "");
                    const onlyDigits = phone.replace(/\D/g, "");
                    const isOnlyDialCode = onlyDigits === meta.country.dialCode;
                    const finalValue = phone.trim() === "" || isOnlyDialCode ? null : phone;
                    setFieldValue(name, finalValue);
                    onChange?.(finalValue ?? "");
                }}
                onBlur={handleBlur}
                disabled={disabled}
                inputProps={{
                    id: id,
                }}
            />
            {isInvalid && (
                <i
                    className="bi bi-exclamation-circle fs-20 position-absolute top-50 end-0 translate-middle-y text-danger p-1 e-0 me-6 fw-bold"
                    style={{ backgroundColor: "inherit" }}
                ></i>
            )}
        </div>
    );
};

export default PhonePicker;
